import { Component, Input, OnInit } from '@angular/core';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import { SubProject } from 'src/app/model/project/sub-project';
import { Project } from 'src/app/model/project/project';
import ProductHelper from 'src/app/helpers/product-helper';
import Asset from 'src/app/model/asset';
import ProductPhoneNumber from 'src/app/model/product/product-phone-number';
import ProductPhoneNumberGroup from 'src/app/display-model/product-phone-number-group';
import Company from 'src/app/model/company/company';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { DO_CHECK_DEBOUNCE_TIME } from 'src/globals/globals';
import { FileHelper } from '../../../../helpers/file-helper';

@Component({
  selector: 'other-product-summary-card',
  templateUrl: './other-product-summary-card.component.html',
  styleUrls: ['./other-product-summary-card.component.scss'],
  providers: [FormatPhoneNumber],
})
export class OtherProductSummaryCardComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  inputOtherProduct: OtherProduct;
  @Input()
  inputSubProject: SubProject;
  @Input()
  inputProject: Project;
  @Input()
  index = 0;
  @Input()
  productPrice: string;
  @Input()
  client = false;
  definitiveDesign: Asset[];

  typeTranslation: string;
  projectContactToCompany = new Map<number, Company>();
  productPhoneNumberGroups: ProductPhoneNumberGroup[] = [];
  multiplePhoneNumbers: boolean = false;
  isImage = FileHelper.isImage;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  ngOnInit() {
    this.typeTranslation = ProductHelper.getTypeOf(this.inputOtherProduct);
    this.calculateProjectContactToCompany();
    this.calculateProductPhoneNumberGroups();
    this.initializeSubscriptions();
  }

  initializeSubscriptions() {
    this.addSubscription(
      this.projectDisplayObservableService.getProductMaterialChangedSubscribable(),
      () => {
        this.typeTranslation = ProductHelper.getTypeOf(this.inputOtherProduct);
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateProjectContactToCompany();
        this.calculateProductPhoneNumberGroups();
      },
      () => {},
      DO_CHECK_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProductPhoneNumberChangedSubscribable(),
      () => {
        this.calculateProjectContactToCompany();
        this.calculateProductPhoneNumberGroups();
      },
      () => {},
      DO_CHECK_DEBOUNCE_TIME
    );
  }

  setDefinitiveDesign() {
    this.definitiveDesign = [];
    if (this.inputOtherProduct.productAssets) {
      for (let pa of this.inputOtherProduct.productAssets) {
        if (pa.formSection === 'internalDesign' && !pa.archived) {
          this.definitiveDesign.push(pa.asset);
        }
      }
    }
  }

  getCompanyName(ppn: ProductPhoneNumber) {
    if (ppn.projectContact != null) {
      for (let pc of this.inputProject.projectContacts) {
        if (ppn.projectContact.id === pc.id) {
          return pc.company.regularName;
        }
      }
    }
  }

  getTitle(title: string) {
    return title == null || title === '' ? 'Geen titel' : title;
  }

  getTypeTranslation(type: string, otherProductName: string) {
    return ProductHelper.getType(type, otherProductName);
  }

  shortenName(name: string) {
    name = name.trim().replace(' ', '');
    let result = name.trim().replace(' ', '');
    let split = name.split('.');
    if (split.length > 1) {
      if (name.length > 8) {
        result =
          split[0].substring(0, 8 - split[split.length - 1].length) +
          '...' +
          split[split.length - 1];
      }
    }
    return result;
  }

  isEmpty(productPhoneNumber: ProductPhoneNumber) {
    return (
      productPhoneNumber.phoneNumbers == null ||
      productPhoneNumber.phoneNumbers === '' ||
      productPhoneNumber.phoneNumbers === 'Geen'
    );
  }

  nonEmptyPhoneNumber(): ProductPhoneNumber[] {
    let result = [];
    for (let ppn of this.inputOtherProduct.productPhoneNumbers) {
      if (!this.isEmpty(ppn)) {
        if (ppn.projectContact != null) {
          result.push(ppn);
        }
      }
    }
    return result;
  }

  atLeastTwoContactsWithPhoneNumber() {
    let companies = new Set<number>();
    for (let ppn of this.inputOtherProduct.productPhoneNumbers) {
      if (!this.isEmpty(ppn)) {
        companies.add(ppn.projectContact.id);
      }
      if (companies.size >= 2) {
        return true;
      }
    }
    return false;
  }

  getPhonesforCompanyName(cn: ProductPhoneNumber): ProductPhoneNumber[] {
    let result = [];
    for (let ppn of this.nonEmptyPhoneNumber()) {
      if (this.getCompanyName(cn) === this.getCompanyName(ppn)) {
        result.push(ppn);
      }
    }
    return result;
  }

  getNonEmptyPhoneNumbersForEachCompany(): ProductPhoneNumber[][] {
    let result = [];
    let companyNames = [];
    for (let nonempty of this.nonEmptyPhoneNumber()) {
      if (!companyNames.includes(this.getCompanyName(nonempty))) {
        companyNames.push(this.getCompanyName(nonempty));
        result.push(this.getPhonesforCompanyName(nonempty));
      }
    }
    return result;
  }

  calculateProjectContactToCompany() {
    this.projectContactToCompany.clear();
    if (CollectionHelper.isPopulated(this.inputProject.projectContacts)) {
      this.inputProject.projectContacts.forEach((pc) => {
        if (pc.company != null) {
          this.projectContactToCompany.set(pc.id, pc.company);
        }
      });
    }
  }

  calculateProductPhoneNumberGroups() {
    if (!this.client || (this.client && this.inputProject.id != null)) {
      this.calculateProductPhoneNumbersWithExistingProjectContacts();
    } else {
      this.calculateProductPhoneNumbersClient();
    }
  }

  calculateProductPhoneNumbersWithExistingProjectContacts() {
    const companyToPhoneNumbers = new Map<number, ProductPhoneNumber[]>();
    if (
      CollectionHelper.isPopulated(this.inputOtherProduct.productPhoneNumbers)
    ) {
      this.inputOtherProduct.productPhoneNumbers.forEach((n) => {
        if (!this.isEmpty(n) && n.projectContact != null) {
          const company = this.projectContactToCompany.get(n.projectContact.id);
          if (company != null) {
            let arr = companyToPhoneNumbers.get(company.id);
            if (arr == null) {
              arr = [];
              companyToPhoneNumbers.set(company.id, arr);
            }
            arr.push(n);
          }
        }
      });
    }
    const groups: ProductPhoneNumberGroup[] = [];
    companyToPhoneNumbers.forEach((arr, companyId) => {
      if (CollectionHelper.isPopulated(arr)) {
        const company = this.projectContactToCompany.get(
          arr[0].projectContact.id
        );
        if (company != null) {
          const group: ProductPhoneNumberGroup = {
            companyId: companyId,
            companyName: company.regularName,
            phoneNumbers: arr.map((n) =>
              this.formatPhoneNumber.transform(n.phoneNumbers)
            ),
          };
          groups.push(group);
        }
      }
    });
    this.productPhoneNumberGroups = groups;
    this.multiplePhoneNumbers =
      groups.length > 1 || groups.some((g) => g.phoneNumbers.length > 1);
  }

  calculateProductPhoneNumbersClient() {
    const groups: ProductPhoneNumberGroup[] = [];

    if (
      CollectionHelper.isPopulated(this.inputProject.projectContacts) &&
      CollectionHelper.isPopulated(this.inputOtherProduct.productPhoneNumbers)
    ) {
      const group: ProductPhoneNumberGroup = {
        companyId: this.inputProject.projectContacts[0].company.id,
        companyName: this.inputProject.projectContacts[0].company.regularName,
        phoneNumbers: this.inputOtherProduct.productPhoneNumbers.map((n) =>
          this.formatPhoneNumber.transform(n.phoneNumbers)
        ),
      };
      groups.push(group);
    }

    this.productPhoneNumberGroups = groups;
    this.multiplePhoneNumbers =
      groups.length > 1 || groups.some((g) => g.phoneNumbers.length > 1);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
